import React, { useContext } from 'react'

import { AxiosError,AxiosPromise } from 'axios'
import cx from 'classnames'
import useSWR, { trigger } from 'swr'

import { A, CircleLoader } from '@driftt/tide-core'

import { ConversationApi } from 'api'
import { MemoReadonlyField } from 'components/DetailsSidebar/Fields'
import { SalesforceContext } from 'context/salesforceContext'
import { ActivityEndUser } from 'modules/accountActivity/domain'

import BaseCard from '../BaseCard'
import { CardHeader, CardHeaderExpandProps } from '../CardHeader'

import utilityStyles from '../../../styles/utility.module.css'
import styles from './styles.module.css'

const NewTab = ({ width = '9', height = '16', className = '' }) => (
  <svg
    width={width}
    height={height}
    className={className}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 3.875C4.37868 3.875 3.875 4.37868 3.875 5V11C3.875 11.6213 4.37868 12.125 5 12.125H11C11.6213 12.125 12.125 11.6213 12.125 11V8.41667C12.125 7.93342 12.5168 7.54167 13 7.54167C13.4832 7.54167 13.875 7.93342 13.875 8.41667V11C13.875 12.5878 12.5878 13.875 11 13.875H5C3.41218 13.875 2.125 12.5878 2.125 11V5C2.125 3.41218 3.41218 2.125 5 2.125H7.74359C8.22684 2.125 8.61859 2.51675 8.61859 3C8.61859 3.48325 8.22684 3.875 7.74359 3.875H5ZM9.20833 3C9.20833 2.51675 9.60008 2.125 10.0833 2.125H13C13.4832 2.125 13.875 2.51675 13.875 3V5.91667C13.875 6.39992 13.4832 6.79167 13 6.79167C12.5168 6.79167 12.125 6.39992 12.125 5.91667V5.16206L8.78897 8.60856C8.45287 8.95579 7.89892 8.96481 7.5517 8.62871C7.20447 8.29261 7.19545 7.73867 7.53154 7.39144L10.9353 3.875H10.0833C9.60008 3.875 9.20833 3.48325 9.20833 3Z"
      fill="#353F45"
    />
  </svg>
)

export type EndUserPropertySource =
  | 'LEGACY'
  | 'DEMANDBASE_IP_LOOKUP'
  | 'DEMANDBASE_DOMAIN_LOOKUP'
  | 'SALESFORCE'
  | 'HUBSPOT'
  | 'CLEARBIT_ENRICHMENT'
  | 'CLEARBIT_REVEAL'
  | 'DRIFT_BOT'
  | 'GEO_IP'
  | 'DRIFT_UTM'
  | 'MADKUDU'
  | 'CLEARBIT_DISCOVERY'
  | 'SALESFORCE_LEAD'
  | 'SALESFORCE_CONTACT'
  | 'SALESFORCE_ACCOUNT'
  | 'SIX_SENSE'
  | 'FULL_CONTACT'
  | 'DRIFT_BOOTSTRAP'
  | 'ZOOM_INFO'
  | 'SIX_SENSE_OEM'
  | 'ZOOM_INFO_OEM'
  | 'CLEARBIT_REVEAL_OEM'
  | 'CLEARBIT_ENRICHMENT_V2'
  | 'CLEARBIT_REVEAL_V2'
  | 'CLEARBIT_ENRICHMENT_V2_OEM'
  | 'CLEARBIT_REVEAL_V2_OEM'
  | 'DRIFT_INTEL_V2'
  | 'CRUNCHBASE_OEM'
  | 'MARKETO_LIST'
  | 'PARDOT_LIST'

export interface RightSidebarCardAttribute {
  displayName: string
  name: string
  enabled: boolean
  source: EndUserPropertySource
  type: string
  displayIndex: number
}

export interface RightSidebarCard {
  id: number
  orgId: number
  displayIndex: number
  cardName: string
  enabled: boolean
  attributes: RightSidebarCardAttribute[]
}

export interface SFDCResponseData {
  baseUrl?: string
  cleanNameToAccountValue?: Record<string, object>
  cleanNameToValue?: Record<string, object>
  endUserSource?: EndUserPropertySource
}

const resolveAttributeValue = (
  attributeDef: RightSidebarCardAttribute,
  sfdcData: SFDCResponseData
) => {
  if (attributeDef.source === 'SALESFORCE_ACCOUNT' && sfdcData.cleanNameToAccountValue) {
    return sfdcData.cleanNameToAccountValue[attributeDef.name]
  }

  return sfdcData.cleanNameToValue && sfdcData.cleanNameToValue[attributeDef.name]
}

const getSalesforceContactData = ({
  endUserId,
  attributeDefs
}: {
  endUserId: number
  attributeDefs: RightSidebarCardAttribute[]
}): AxiosPromise<any> => {
  return ConversationApi.post(
    `/salesforce/sync/sidebar/refresh?enduserid=${endUserId}`,
    attributeDefs
  )
}

export const useSalesforceContactData = ({
  endUserId,
  endUserEmail,
  attributeDefs
}: {
  endUserId: number
  endUserEmail?: string
  attributeDefs: any
}): { data?: SFDCResponseData; errorCode?: string } => {
  const { data, error } = useSWR<SFDCResponseData, AxiosError>(
    `salesforceContactData-${endUserId}`,
    async () => {
      if (!endUserEmail) {
        return null
      }
      const { data } = await getSalesforceContactData({ endUserId, attributeDefs })
      return data
    },
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  )

  if (!endUserEmail) {
    return { data, errorCode: '404' }
  }
  return { data, errorCode: error?.code }
}

export const SFDCHeader: React.FC<CardHeaderExpandProps> = ({ ...rest }) => (
  <CardHeader content="Salesforce" data-ocweb-id="SFDCCardHeader" {...rest} />
)

export const SFDCCard: React.FC<{ contact: ActivityEndUser }> = ({ contact }) => {
  const endUserId = contact.id
  const endUserEmail = contact.email

  const attributeDefs = useContext(SalesforceContext)

  const { data: sfdcData, errorCode } = useSalesforceContactData({
    endUserId,
    endUserEmail,
    attributeDefs
  })

  const isLoading = !sfdcData && !errorCode

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <CircleLoader />
      </div>
    )
  }

  return (
    <BaseCard data-ocweb-id="SFDCCard">
      {isLoading && (
        <div className={styles.loading}>
          <CircleLoader />
        </div>
      )}

      {!isLoading && errorCode && errorCode !== '404' && (
        <div
          data-ocweb-id="SFDCCardError"
          className={cx(utilityStyles.smFontSize, styles.errorMessage)}>
          {"We couldn't load data from Salesforce."}
          <span onClick={() => trigger(endUserId.toString())} className={styles.refreshButton}>
            Refresh
          </span>
        </div>
      )}

      {!isLoading && errorCode === '404' && (
        <div
          data-ocweb-id="SFDCCardNotFound"
          className={cx(utilityStyles.smFontSize, styles.errorMessage)}>
          {"We couldn't find this contact in Salesforce. "}
        </div>
      )}
      {sfdcData && (
        <>
          <div className={styles.fields}>
            {attributeDefs.map((attributeDef: any, i) => (
              <MemoReadonlyField
                key={`${attributeDef.name}-${i}`}
                label={attributeDef.displayName}
                value={resolveAttributeValue(attributeDef, sfdcData) || '–'}
              />
            ))}
          </div>

          {sfdcData?.baseUrl && (
            <div>
              <A
                className={styles.openInSalesforce}
                href={sfdcData.baseUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-ocweb-id="ViewInSalesforceLink">
                {'View in Salesforce '}
                <NewTab width="12" height="12" className={styles.openInSalesforceIcon} />
              </A>
            </div>
          )}
        </>
      )}
    </BaseCard>
  )
}
